import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {BehaviorSubject, Subscription} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {SigningProcessStep} from '../../_enums/signing-process-step.enum';
import {SigningProcess} from '../../_models/signing-process.model';
import {SigningProcessStepService} from '../../_services/signing-process-steps.service';
import {SigningProcessService} from '../../_services/signing-process.service';
import {UIService} from '../../_services/ui.service';
import {StyleServiceService} from '../../_services/style-service.service';
import {RestApiService} from '../../_services/rest-api.service';
import {Step} from '../../_enums/step.enum';
import {LogType} from '../../_enums/log-type.enum';

@Component({
	selector: 'x-pep-form',
	templateUrl: './pep-form.component.html',
	styleUrls: ['./pep-form.component.css'],
})
export class PepFormComponent implements OnInit {
	public useCredilowStyle!: boolean;
	@Output() changeStepperState: EventEmitter<undefined> = new EventEmitter<undefined>();

	public loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public pepForm: FormGroup;
	public isPep: boolean = false;
	public today: string = new Date().toISOString().split('T')[0];

	private signingProcess!: SigningProcess;
	private signingProcessSubscription!: Subscription;

	public readonly PEP_TEXT = `DECLARACIÓN JURADA – PERSONA EXPUESTA POLÍTICAMENTE (PEP)

El Cliente declara bajo juramento que NO reviste ni ha revestido en los últimos años la condición de Persona Expuesta Políticamente, ni es familiar directo ni persona cercana de una Persona Expuesta Políticamente.

A los efectos del presente, se considera Persona Expuesta Políticamente a quien desempeña o ha desempeñado funciones públicas relevantes, tales como funcionarios del Estado nacional, provincial o municipal, legisladores, magistrados, autoridades de fuerzas de seguridad, o directivos de empresas u organismos públicos, así como también a sus familiares directos y personas cercanas.

El Cliente se obliga a informar cualquier cambio en la información declarada durante la vigencia de la relación contractual.`;

	constructor(
		public styleService: StyleServiceService,
		private fb: FormBuilder,
		private readonly signingProcessService: SigningProcessService,
		private readonly signingProcessStepService: SigningProcessStepService,
		private readonly uIService: UIService,
		private restApiService: RestApiService
	) {
		this.pepForm = this.fb.group({
			is_pep: [false, Validators.required],
			cargo_funcion: [''],
			organismo_entidad: [''],
			periodo_desde: [''],
			relacion: [''],
		});
	}

	public ngOnInit(): void {
		this.styleService.useCredilowStyle.subscribe((useCredilowStyle: boolean) => {
			this.useCredilowStyle = useCredilowStyle;
		});
		this.signingProcessSubscription = this.signingProcessService.getSigningProcess().subscribe({
			next: (signingProcess: SigningProcess) => {
				this.signingProcess = signingProcess;
				this.restApiService
					.monitoringLog({creditFlowId: this.signingProcess.creditFlowId, step: Step.PepForm, logType: LogType.Flujo})
					.subscribe(() => {
						this.unsubscribe();
					});
			},
			error: (error: Error) => console.log(error),
		});
	}

	private unsubscribe(): void {
		if (this.signingProcessSubscription && !this.signingProcessSubscription.closed) {
			this.signingProcessSubscription.unsubscribe();
		}
	}

	public onIsPepChange(): void {
		this.isPep = this.pepForm.get('is_pep')?.value === true;
		if (this.isPep) {
			this.pepForm.get('cargo_funcion')?.setValidators([Validators.required]);
			this.pepForm.get('organismo_entidad')?.setValidators([Validators.required]);
			this.pepForm.get('periodo_desde')?.setValidators([Validators.required]);
		} else {
			this.pepForm.get('cargo_funcion')?.clearValidators();
			this.pepForm.get('organismo_entidad')?.clearValidators();
			this.pepForm.get('periodo_desde')?.clearValidators();
		}
		this.pepForm.get('cargo_funcion')?.updateValueAndValidity();
		this.pepForm.get('organismo_entidad')?.updateValueAndValidity();
		this.pepForm.get('periodo_desde')?.updateValueAndValidity();
	}

	public submitPep(): void {
		if (this.pepForm.invalid) {
			this.uIService.errorMessage('Error', 'Por favor complete todos los campos requeridos');
			return;
		}

		this.loading.next(true);
		const formData = {
			...this.pepForm.value,
			texto_mostrado: this.PEP_TEXT,
		};

		this.signingProcessService
			.submitPep(formData)
			.pipe(finalize(() => this.loading.next(false)))
			.subscribe({
				next: (response: any) => {
					if (response.status === 'success') {
						this.restApiService
							.monitoringLog({creditFlowId: this.signingProcess.creditFlowId, step: Step.PepFormSubmit, logType: LogType.Flujo})
							.subscribe();
						this.uIService.successMessage('Exitoso!', 'Declaración PEP guardada correctamente');
						this.next();
					} else {
						this.uIService.errorMessage('Error', response.message || 'Error al guardar la declaración');
					}
				},
				error: (error: any) => {
					this.restApiService
						.monitoringLog({creditFlowId: this.signingProcess.creditFlowId, step: Step.PepFormSubmit, logType: LogType.Consola, error})
						.subscribe();
					this.uIService.errorMessage('Error', error.error?.message || 'Error al guardar la declaración');
				},
			});
	}

	public next(): void {
		this.changeStepperState.emit();
		this.signingProcessStepService.moveTo(SigningProcessStep.SignaturePad);
	}
}
